import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  NotificationListTableComponent,
  NotificationViewModel,
} from './notification-list-table/notification-list-table.component';
import { MatCardModule } from '@angular/material/card';
import {
  EditedNotificationViewModel,
  NotificationEditDialogComponent,
  NotificationEditorDialogViewModel,
} from './notification-edit-dialog/notification-edit-dialog.component';
import { firstValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { NotificationService } from '@services/api/notification.service';
import { NotificationRequest } from '@entities/notification';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatDatepickerModule,
  MatDateRangePicker,
} from '@angular/material/datepicker';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { ja } from 'date-fns/locale';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonToggle } from '@angular/material/button-toggle';
import { MatButtonModule } from '@angular/material/button';
import { subDays } from 'date-fns';

@Component({
  selector: 'app-notification-list',
  standalone: true,
  providers: [
    {
      provide: MAT_DATE_LOCALE,
      useValue: ja,
    },
    provideDateFnsAdapter({
      parse: {
        dateInput: 'yyyy/MM/dd',
      },
      display: {
        dateInput: 'yyyy/MM/dd',
        monthYearLabel: 'yyyy/MM',
        dateA11yLabel: 'yyyy/MM/dd',
        monthYearA11yLabel: 'yyyy/MM',
      },
    }),
  ],
  imports: [
    CommonModule,
    NotificationListTableComponent,
    MatCardModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatDatepickerModule,
    MatDateRangePicker,
    MatIconModule,
    MatButtonToggle,
    MatButtonModule,
    MatLabel,
  ],
  templateUrl: './notification-list.component.html',
  styleUrl: './notification-list.component.scss',
})
export class NotificationListComponent implements OnInit {
  /** 検索フォーム */
  public historyFormGroup: FormGroup;

  // 疎通処理実装後ダミーデータ削除
  public notificationList: NotificationViewModel[] = [];

  // 取得した掲示板メッセージ情報のうちステータスがとりうる値から構成された配列
  public statusList: string[] = ['投稿済み', '投稿予約'];

  /** 対象期間(開始) */
  public historyFrom: Date = new Date();
  /** 対象期間(終了) */
  public historyTo: Date | null = new Date();

  private dateRangeValidator: ValidatorFn = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const start = group.get('historyFrom')?.value;
    const end = group.get('historyTo')?.value;
    return start && end && start > end ? { dateRange: true } : null;
  };

  constructor(
    private dialog: MatDialog,
    private notificationService: NotificationService,
  ) {
    this.historyFormGroup = new FormGroup(
      {
        historyFrom: new FormControl<Date>(subDays(new Date(), 183), {
          validators: [Validators.required],
        }),
        historyTo: new FormControl<Date | null>(null, {}),
      },
      { validators: this.dateRangeValidator },
    );
  }

  public ngOnInit(): void {
    // 報告書メッセージ一覧を取得する
    this.historyFormGroupValueChanged();
    this.initNotificationDetailList();
  }

  /**
   * 掲示板メッセージ追加/編集ダイアログを表示する
   * @param notification テナント
   */
  public async openNotificationEditDialog(
    notification?: NotificationViewModel,
  ) {
    const options: NotificationEditorDialogViewModel = {
      updating: false,
    };

    if (notification) {
      options.updating = true;
      options.target = {
        messageId: notification.messageId,
        title: notification.title,
        content: notification.content,
        releaseAt: notification.releaseAt,
      };
    }

    const editedNotification: EditedNotificationViewModel =
      await firstValueFrom(
        this.dialog
          .open(NotificationEditDialogComponent, {
            width: '700px',
            data: options,
          })
          .afterClosed(),
      );

    if (!editedNotification) {
      return;
    }

    if (options.updating) {
      // 更新処理
      const updatedNotificationRequest: NotificationRequest =
        editedNotification;
      this.notificationService.update(updatedNotificationRequest).subscribe({
        next: () => {
          this.initNotificationDetailList();
        },
        error: () => {
          const alertDialogOptions: AlertDialogOption = {
            type: 'error',
            message: `掲示板メッセージ更新に失敗しました。\nしばらくしてからもう一度お試しください。`,
          };
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
        },
      });
    } else {
      // 追加処理
      const addedNotificationRequest: NotificationRequest = editedNotification;
      this.notificationService.add(addedNotificationRequest).subscribe({
        next: () => {
          this.initNotificationDetailList();
        },
        error: () => {
          const alertDialogOptions: AlertDialogOption = {
            type: 'error',
            message: `掲示板メッセージ追加に失敗しました。\nしばらくしてからもう一度お試しください。`,
          };
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
        },
      });
    }
  }

  /**
   * 掲示物メッセージを削除する
   * @param notification 掲示物メッセージ
   */
  public async deleteNotification(notification: NotificationViewModel) {
    const options: AlertDialogOption = {
      type: 'error',
      title: 'Confirm',
      message: `本当に掲示板メッセージ「${notification.title}」を削除しますか？`,
      operation: '削除',
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    // 掲示物メッセージを削除する
    this.notificationService.delete(notification.messageId).subscribe({
      next: () => {
        this.initNotificationDetailList();
      },
      error: () => {
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message: `掲示板メッセージ削除に失敗しました。\nしばらくしてからもう一度お試しください。`,
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
      },
    });
  }

  public initNotificationDetailList(): void {
    this.historyFrom = this.historyFormGroup.get('historyFrom')?.value;
    this.historyTo = this.historyFormGroup.get('historyTo')?.value;

    this.historyFrom.setHours(0, 0, 0, 0);

    if (this.historyTo instanceof Date) {
      this.historyTo.setHours(23, 59, 59, 0);
    }

    this.notificationService
      .findList(this.historyFrom, this.historyTo)
      .subscribe({
        next: (notificationList) => {
          this.notificationList = notificationList;
        },
      });
  }

  /**
   * 対象期間の変更検知を購読する
   */
  private historyFormGroupValueChanged(): void {
    type historyViewModel = {
      historyFrom: Date;
      historyTo: Date;
    };
    this.historyFormGroup.valueChanges.subscribe(
      (history: historyViewModel) => {
        this.historyFrom = history.historyFrom;
        this.historyTo = history.historyTo;
      },
    );
  }
}
